import React, { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ProtectedRoute } from "./ProtectedRoute";
import { MaintenanceGuard } from "./MaintenanceGuard";
import { AuthRedirect } from "./AuthRedirect";
import PublicRouteLayout from "./PublicRouteLayout";
import { preloadProfileRoute } from "./profile-route-loader";
import { AppLoadingScreen } from "../../components/AppLoadingScreen";
import ErrorBoundary from "../../components/Common/ErrorFallback";
import {
  LoginPageSkeleton,
  MaintenancePageSkeleton,
  DocPageSkeleton,
  PartyInvitePageSkeleton,
  NotFoundPageSkeleton,
  ProfilePageSkeleton,
  SettingsPageSkeleton,
  SecureChatListPageSkeleton,
  SecureChatRoomPageSkeleton,
} from "../../components/PageSkeletons";
import {
  loadLoginPage,
  loadProfilePage,
  loadSettingsPage,
} from "./primary-page-loaders";

import SecureChatListPage from "../(primary)/secure-chat/page";

const RootRouteLayout = lazy(async () => await import("./RootRouteLayout"));
const PrimaryNavigationLayout = lazy(
  async () => await import("./PrimaryNavigationLayout")
);
const MainLayout = lazy(
  async () => await import("../../secure-chat/Layout/MainLayout")
);
const SecureChatLayout = lazy(
  async () => await import("../(primary)/secure-chat/layout")
);
const ChatRoomRoute = lazy(async () => await import("./ChatRoomRoute"));

// Lazy load pages for code splitting
const LoginPage = lazy(loadLoginPage);
const ProfilePage = lazy(loadProfilePage);
const MaintenancePage = lazy(
  async () => await import("../../app/maintenance/page")
);
const TermsPage = lazy(async () => await import("../../app/terms/page"));
const PolicyPage = lazy(async () => await import("../../app/policy/page"));
const SettingsPage = lazy(loadSettingsPage);
const PartyInvitePage = lazy(
  async () => await import("../../app/party/invite/[code]/page")
);
const NotFoundPage = lazy(async () => await import("../../app/not-found"));

// Wrap router creation with Sentry for route tracking (React Router v6)
let createRouter = createBrowserRouter;
try {
  if (Sentry.wrapCreateBrowserRouterV6) {
    createRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
  } else {
    console.warn(
      "Sentry.wrapCreateBrowserRouterV6 not available, using default createBrowserRouter."
    );
  }
} catch (error) {
  console.warn("Failed to wrap router with Sentry, using default:", error);
}

type RouterOpts = NonNullable<Parameters<typeof createRouter>[1]>;
// v7_startTransition is supported at runtime; types in this RR version don't include it
const routerOpts: RouterOpts = {
  /* eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- future flag not in FutureConfig in this RR version */
  future: { v7_startTransition: true } as RouterOpts["future"],
};

export const appRoutes: RouteObject[] = [
  {
    element: <PublicRouteLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <MaintenanceGuard />,
        children: [
          {
            path: "/party/invite/:code",
            element: (
              <Suspense fallback={<PartyInvitePageSkeleton />}>
                <PartyInvitePage />
              </Suspense>
            ),
          },
          {
            path: "/maintenance",
            element: (
              <Suspense fallback={<MaintenancePageSkeleton />}>
                <MaintenancePage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "*",
        element: (
          <Suspense fallback={<NotFoundPageSkeleton />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "/",
    element: (
      <Suspense fallback={<AppLoadingScreen />}>
        <RootRouteLayout />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="/login" replace />,
      },
      {
        path: "login",
        element: (
          <AuthRedirect>
            <Suspense fallback={<LoginPageSkeleton />}>
              <LoginPage />
            </Suspense>
          </AuthRedirect>
        ),
      },
      {
        path: "terms",
        element: (
          <Suspense fallback={<DocPageSkeleton />}>
            <TermsPage />
          </Suspense>
        ),
      },
      {
        path: "policy",
        element: (
          <Suspense fallback={<DocPageSkeleton />}>
            <PolicyPage />
          </Suspense>
        ),
      },
      {
        element: <MaintenanceGuard />,
        children: [
          {
            element: <PrimaryNavigationLayout />,
            children: [
              {
                element: <MainLayout />,
                children: [
                  {
                    path: "profile/:address",
                    loader: ({ params }) => preloadProfileRoute(params.address),
                    element: (
                      <Suspense fallback={<ProfilePageSkeleton />}>
                        <ProfilePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "edit-profile",
                    lazy: async () => await import("./EditProfileRoute"),
                  },
                  {
                    path: "settings",
                    element: (
                      <ProtectedRoute>
                        <Suspense fallback={<SettingsPageSkeleton />}>
                          <SettingsPage />
                        </Suspense>
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
              {
                path: "secure-chat",
                element: (
                  <ProtectedRoute>
                    <Suspense fallback={<SecureChatListPageSkeleton />}>
                      <SecureChatLayout />
                    </Suspense>
                  </ProtectedRoute>
                ),
                children: [
                  {
                    index: true,
                    element: <SecureChatListPage />,
                  },
                  {
                    path: ":roomId",
                    element: (
                      <Suspense fallback={<SecureChatRoomPageSkeleton />}>
                        <ChatRoomRoute />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createRouter(appRoutes, routerOpts);
